import axios from 'axios';
import useSWR from 'swr';
import _ from 'lodash';
import { clearLocalStoragePreservingPrefs } from '../utils/clearLocalStoragePreservingPrefs';
import { buildLoginPath } from '../utils';
import { installAxiosMock } from '../api/mock';

const base = process.env.REACT_APP_SPARA_API_URL || 'https://api.spara.is';

const api = axios.create({
    baseURL: base + '/api',
    headers: {
        'Content-Type': 'application/json',
        'APP-KEY': '3f8a97e6-51ea-44d7-a9c9-87ae6040f7fb',
        'Cache-Control': 'no-store',
    },
});

installAxiosMock(api);

// Add authentication interceptor
api.interceptors.request.use((request) => {
    let store = localStorage.user ? JSON.parse(localStorage.getItem('user')) : '';
    if (store) {
        request.headers.Authorization = 'Bearer ' + store.access_token;
    }

    // Add abort controller to request config
    if (!request.signal) {
        request.signal = new AbortController().signal;
    }

    return request;
});

let isRedirectingToLogin = false;

api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (!axios.isCancel(error)) {
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                const hasToken = !!localStorage.getItem('user');
                console.warn(
                    '🔒 [lib/api] Got',
                    error.response.status,
                    'from:',
                    error.config?.method?.toUpperCase(),
                    error.config?.url
                );
                console.warn(
                    '🔒 [lib/api] hasToken:',
                    hasToken,
                    '| isRedirecting:',
                    isRedirectingToLogin
                );
                console.trace('🔒 [lib/api] Stack trace for', error.response.status);
                if (error?.response?.status === 401 && hasToken && !isRedirectingToLogin) {
                    isRedirectingToLogin = true;
                    console.warn(
                        '🔒 [lib/api] >>> CLEARING localStorage and redirecting to /auth/login'
                    );
                    clearLocalStoragePreservingPrefs();
                    window.location = buildLoginPath();
                }
            }
            return Promise.reject(error);
        }
        return Promise.reject(error);
    }
);

// Create a map to store abort controllers for each request
const abortControllers = new Map();

export const fetcher = async (url, method = 'GET', data = null) => {
    try {
        // Abort any existing request for this URL (bara fyrir GET, því POST/PUT/DELETE eru yfirleitt ekki idempotent)
        if (method === 'GET' && abortControllers.has(url)) {
            abortControllers.get(url).abort();
        }

        let controller;
        let timeoutId;
        if (method === 'GET') {
            controller = new AbortController();
            abortControllers.set(url, controller);
            timeoutId = setTimeout(() => {
                controller.abort();
                abortControllers.delete(url);
            }, 30000);
        }

        let response;
        if (method === 'GET') {
            response = await api.get(url, { signal: controller.signal });
        } else if (method === 'POST') {
            response = await api.post(url, data);
        } else if (method === 'PUT') {
            response = await api.put(url, data);
        } else if (method === 'DELETE') {
            response = await api.delete(url, { data });
        } else {
            throw new Error('Óstudd aðferð: ' + method);
        }

        if (method === 'GET') {
            clearTimeout(timeoutId);
            abortControllers.delete(url);
        }

        return response.data;
    } catch (error) {
        if (method === 'GET' && error.name === 'AbortError') {
            console.log('Request was aborted:', url);
        }
        if (method === 'GET') {
            abortControllers.delete(url);
        }
        throw error;
    }
};

export const quietFetcher = async (url) => {
    const response = await api.get(url);
    return response.data;
};

export const buildQueryString = (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value.toString().trim());
        }
    });

    return queryParams.toString() ? `?${queryParams.toString()}` : '';
};

export const companyApi = {
    list: (params) => `/company${buildQueryString(params)}`,
    getById: (id) => `/company/${id}`,
    getByKennitala: (kennitala) => `/company/company_by_kennitala/${kennitala}`,
    createFrom1819: '/company/from_1819',
    quickCreate: '/company/quick',
    changeStatus: '/company/disable_enable_company',
    create: '/company',
    update: (id) => `/company/${id}`,
    delete: (id) => `/company/${id}`,
};

export const regionApi = {
    list: '/region',
};

export const eventApi = {
    list: (params) => `/events${buildQueryString(params)}`,
    getById: (id) => `/events/${id}`,
    create: () => '/events',
    update: (id) => `/events/${id}`,
    delete: (id) => `/events/${id}`,
    responses: (id) => `/events/${id}/responses`,
    responsesCsv: (id) => `/events/${id}/responses?format=csv`,
    attendance: (id) => `/events/${id}/attendance`,
    undoAttendance: (id, userId) => `/events/${id}/attendance/${userId}`,
    notificationCount: (id, params) => `/events/${id}/notification/count${buildQueryString(params)}`,
    notificationSend: (id) => `/events/${id}/notification/send`,
};

export const groupApi = {
    list: (params) => `/special_company${buildQueryString(params)}`,
    getById: (id) => `/special_company/${id}`,
    create: `/special_company`,
    update: (id) => `/special_company/${id}`,
    delete: (id) => `/special_company/${id}`,
    hrSync: (id) => `/special_company/${id}/hr_sync`,
    hrSyncRuns: (id) => `/special_company/${id}/hr_sync/runs`,
    hrSyncPayUnions: (id) => `/special_company/${id}/hr_sync/payunions`,
};

export const specialUserApi = {
    list: (params) => {
        const formattedParams = {
            ...params,
            group_ids: params.group_ids?.join(','),
        };
        return `/special_user${buildQueryString(formattedParams)}`;
    },
    search: (params) => {
        const formattedParams = {
            ...params,
            group_ids: params.group_ids?.join(','),
        };
        return `/special_user${buildQueryString(formattedParams)}`;
    },
    create: () => '/special_user/',
    update: (id) => `/special_user/${id}/`,
    delete: (id) => `/special_user/${id}/`,
    deleteByIds: () => '/special_user/delete_by_ids',
    batch: (id) => `/special_user/batch/${id}`,
    upsertMembershipPhoto: () => '/special_user/membership_photo',
    deleteMembershipPhoto: () => '/special_user/membership_photo',
};

export const dashboardApi = {
    basicCounts: '/dashboard/basic_counts',
    totalCountCU: (companyId) => `/dashboard/total_count_cu/${companyId}`,
    cuCounts: (companyId) => `/dashboard/total_count_cu/${companyId}/counts`,
    cuActivity: (companyId) => `/dashboard/total_count_cu/${companyId}/activity`,
    cuDemographics: (companyId) => `/dashboard/total_count_cu/${companyId}/demographics`,
    cuLocations: (companyId) => `/dashboard/total_count_cu/${companyId}/locations`,
    group: (groupId) => `/dashboard/group/${groupId}`,
    groupCounts: (groupId) => `/dashboard/group/${groupId}/counts`,
    groupActivity: (groupId) => `/dashboard/group/${groupId}/activity`,
    groupDemographics: (groupId) => `/dashboard/group/${groupId}/demographics`,
    groupLocations: (groupId) => `/dashboard/group/${groupId}/locations`,
    userDemographics: '/dashboard/user_demographics',
    newUsersStatistics: (params) => `/dashboard/new_users_statistics${buildQueryString(params)}`,
    offerActivityStatistics: (params) =>
        `/dashboard/offer_activity_statistics${buildQueryString(params)}`,
    combinedActivityData: '/dashboard/combined_activity_data',
    analyticsLive: '/dashboard/analytics/live',
    geographicData: '/dashboard/geographic_data',
    specialCompanyData: '/dashboard/special_company_data',
    manageOfferStatus: '/dashboard/manage_offer_status',
    groupMemberGroups: (groupId) => `/dashboard/group/${groupId}/member_groups`,
    groupTopOffers: (groupId, days, includeInactive) => {
        const params = new URLSearchParams();
        if (days) params.set('days', days);
        if (includeInactive) params.set('include_inactive', 'true');
        const qs = params.toString();
        return `/dashboard/group/${groupId}/top_offers${qs ? `?${qs}` : ''}`;
    },
    topOffers: (days, includeInactive, companyId) => {
        const params = new URLSearchParams();
        if (days) params.set('days', days);
        if (includeInactive) params.set('include_inactive', 'true');
        if (companyId) params.set('company_id', companyId);
        const qs = params.toString();
        return `/dashboard/top_offers${qs ? `?${qs}` : ''}`;
    },
};

export const offerApi = {
    list: (params) => `/offers${buildQueryString(params)}`,
    hasGeneral: () => '/offers/has_general',
    getById: (id) => `/offer/${id}`,
    search: (search) => `/offer/search/${search}`,
    getByCompany: (companyId) => `/offer/offers_by_company_user/${companyId}`,
    getByCompaniesAndGroups: (companyIds, groupIds) => {
        const params = {};
        if (companyIds) params.company_ids = companyIds;
        if (groupIds) params.group_ids = groupIds;
        return `/offers${buildQueryString(params)}`;
    },
    getBySpecialCompany: (specialCompanyId) => `/offer/by_special_company/${specialCompanyId}`,
    getReport: (offerId) => `/offer/report/${offerId}`,
    getTopOffers: () => `/top_offers?filter=admin`,
    create: () => '/offer',
    update: (id) => `/offer/${id}`,
    delete: (id) => `/offer/${id}`,
    changeStatus: () => '/offer/change_status',
    changeStatusArchive: () => '/offer/change_status_archive',
    notificationCount: (id, options = {}) => {
        const params = new URLSearchParams();
        // Backwards compatible: second arg used to be a boolean
        if (typeof options === 'boolean') {
            if (options) params.set('include_unregistered', '1');
        } else {
            if (options.includeUnregistered) params.set('include_unregistered', '1');
            if (options.groupIds) params.set('group_ids', options.groupIds);
        }
        const qs = params.toString();
        return `/offer/${id}/notification/count${qs ? `?${qs}` : ''}`;
    },
    notificationSend: (id) => `/offer/${id}/notification/send`,
};

export const companyUserApi = {
    list: '/company_user',
    listByIds: (companyIds, groupIds) => {
        const params = new URLSearchParams();
        if (companyIds?.length) params.set('company_ids', companyIds.join(','));
        if (groupIds?.length) params.set('group_ids', groupIds.join(','));
        const qs = params.toString();
        return qs ? `/company_user?${qs}` : null;
    },
    listWithFilter: '/company_user?filter=company_user',
    getByUser: (userId) => `/company_user/user/${userId}`,
    create: '/company_user',
    update: (id) => `/company_user/${id}`,
};

const groupCompanyUsers = (results) =>
    _.chain(results)
        .groupBy('torgid_user_id')
        .map((value, key) => ({ parent_id: key, children: value }))
        .value();

export const useCompanyUsers = (enabled = true) => {
    const { data, error, isLoading, mutate } = useSWR(enabled ? companyUserApi.list : null, fetcher);
    return {
        companyUsers: data ? groupCompanyUsers(data.results) : [],
        count: data?.count ?? 0,
        loading: isLoading,
        error,
        mutate,
    };
};

export const useCompanyUsersByCompanies = (companyIds) => {
    const ids = companyIds?.filter(Boolean) ?? [];
    const key = ids.length ? companyUserApi.listByIds(ids) : null;
    const { data, error, isLoading, mutate } = useSWR(key, fetcher);
    return {
        companyUsers: data
            ? groupCompanyUsers(data.results.filter((u) => !u.hidden))
            : [],
        count: data?.count ?? 0,
        loading: isLoading,
        error,
        mutate,
    };
};

export const useCompanyUsersForRole = () => {
    const userStore = JSON.parse(localStorage.getItem('user') || '{}');
    const roles = userStore.roles ?? [];
    const isAdmin = roles.includes('super_admin');

    const companyIds = (userStore.companyIds ?? []).filter(Boolean);
    const groupIds = (userStore.groups ?? []).map((g) => g.id).filter(Boolean);
    const hasIds = companyIds.length > 0 || groupIds.length > 0;

    const adminKey = isAdmin ? companyUserApi.list : null;
    const cuKey = !isAdmin && hasIds ? companyUserApi.listByIds(companyIds, groupIds) : null;
    const key = isAdmin ? adminKey : cuKey;

    const { data, error, isLoading, mutate } = useSWR(key, fetcher);
    return {
        companyUsers: data ? groupCompanyUsers(data.results) : [],
        count: data?.count ?? 0,
        loading: isLoading,
        error,
        mutate,
    };
};

export const useCompanyUsersWithFilter = () => {
    const { data, error, isLoading, mutate } = useSWR(companyUserApi.listWithFilter, fetcher);
    return {
        companyUsers: data ? groupCompanyUsers(data.results) : [],
        count: data?.count ?? 0,
        loading: isLoading,
        error,
        mutate,
    };
};

export const appUserApi = {
    list: (page = 1) => `/user?page=${page}`,
    search: (query, page = 1) => `/user/search/${encodeURIComponent(query)}?page=${page}`,
    get: (id) => `/user/${id}`,
    report: (id) => `/user/report/${id}`,
    create: '/user',
    update: (id) => `/user/${id}`,
    delete: (id) => `/user/${id}`,
    notificationSend: (id) => `/user/${id}/notification/send`,
};

export const loginActivityApi = {
    list: (page = 1, limit = 20, filters = {}) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (filters.login_method) params.set('login_method', filters.login_method);
        if (filters.login_type) params.set('login_type', filters.login_type);
        if (filters.status) params.set('status', filters.status);
        return `/login_activity?${params.toString()}`;
    },
};

export const jobBatchApi = {
    list: ({ page = 1, limit = 30, status } = {}) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (status) params.set('status', status);
        return `/job_batches?${params.toString()}`;
    },
    get: (batchId) => `/job_batches/${encodeURIComponent(batchId)}`,
    cancel: (batchId) => `/job_batches/${encodeURIComponent(batchId)}/cancel`,
};

export const failedJobApi = {
    list: ({ page = 1, limit = 30, search, queue } = {}) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (search) params.set('search', search);
        if (queue) params.set('queue', queue);
        return `/failed_jobs?${params.toString()}`;
    },
};

export const queueHealthApi = {
    get: () => '/queue_health',
};

export const securityEventApi = {
    list: ({ page = 1, limit = 30, type } = {}) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (type) params.set('type', type);
        return `/security_events?${params.toString()}`;
    },
};

export const publicApiAuditLogApi = {
    list: ({
        page = 1,
        limit = 40,
        outcome,
        http_status,
        customer,
        special_company_id,
        access_token_id,
        member_identifier_hash,
        from,
        to,
    } = {}) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (outcome) params.set('outcome', outcome);
        if (http_status) params.set('http_status', String(http_status));
        if (customer) params.set('customer', customer);
        if (special_company_id) params.set('special_company_id', String(special_company_id));
        if (access_token_id) params.set('access_token_id', String(access_token_id));
        if (member_identifier_hash) params.set('member_identifier_hash', member_identifier_hash);
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        return `/public_api_audit_logs?${params.toString()}`;
    },
};

export const notificationApi = {
    list: ({ page = 1, limit = 30, kind, source, status } = {}) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (kind) params.set('kind', kind);
        if (source) params.set('source', source);
        if (status) params.set('status', status);
        return `/notifications?${params.toString()}`;
    },
};

export const userApi = {
    update: (id) => `/user/${id}`,
    changeEmail: '/user/change_email',
    changePhone: '/user/change_phone',
    verifyByCode: '/user/verify_by_code',
};

export const authApi = {
    me: '/auth/me',
    passkeys: '/auth/webauthn/credentials',
    passkeyRegisterOptions: '/auth/webauthn/register/options',
    passkeyRegister: '/auth/webauthn/register',
    passkeyReauthPassword: '/auth/webauthn/reauth/password',
    passkeyReauthAudkenni: '/auth/webauthn/reauth/audkenni',
    passkeyReauthAudkenniComplete: '/auth/webauthn/reauth/audkenni/complete',
    passkeyUpdate: (id) => `/auth/webauthn/credentials/${id}`,
    passkeyDelete: (id) => `/auth/webauthn/credentials/${id}`,
};

export const companyCategoryApi = {
    list: '/company_category',
    create: '/company_category',
    update: (id) => `/company_category/${id}`,
    delete: (id) => `/company_category/${id}`,
};

export const offerCategoryApi = {
    list: '/category',
    create: '/category',
    update: (id) => `/category/${id}`,
    delete: (id) => `/category/${id}`,
};

export const geocodingApi = {
    geocode: (address) => `/geocoding/geocode?address=${encodeURIComponent(address)}`,
};

export default api;
